'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, ApiRequestError, getApiErrorMessage } from '@/lib/api';
import {
  MANUAL_LIMITS,
  isNothingToReleaseRefusal,
  isRcSilenceRefusal,
  refusalSlowLink,
  type ControlFrame,
  type ManualClampReason,
  type ManualEnvelope,
} from '@/lib/manual-vector';
import type {
  DroneAckPayload,
  ManualControlSnapshot,
  ManualControlStatePayload,
} from '@/types/coordination';

/**
 * Manual flight control — the session, the wire, and the dead-man.
 *
 * This hook owns everything in `docs/PLANS/manual-flight-control/WIRE-CONTRACT.md`
 * that is NOT geometry: asking the RC for authority, holding it with a 1 Hz
 * heartbeat, streaming velocity setpoints inside the control plane's rate
 * ceiling, and giving authority back. The pixels-to-vector half lives in
 * `lib/manual-vector.ts` and never touches the network; this half never does
 * geometry. Neither can be tested through the other, which is the point.
 *
 * It mirrors `useDroneCommand` in shape — one controller object for whichever
 * aircraft is selected, keyed by id inside the hook — but the lifecycle is
 * completely different and deliberately so. A flight command is one-shot and
 * fire-and-track; a manual session is a lease on the aircraft that must be given
 * back, and every path out of it, including the ones nobody plans for, has to
 * end with the aircraft stopped.
 *
 * The three rules the whole file is arranged around:
 *
 * 1. **Release means stop first.** Every exit — the operator's, an unmount, a
 *    drone switch — sends `hover` before `release`. The RC zeroes the sticks on
 *    a release too, but a COP that hands back a moving aircraft and hopes is not
 *    a command surface.
 * 2. **Nothing silently "probably worked".** A grant that goes unanswered for
 *    `GRANT_TIMEOUT_MS` returns to IDLE carrying the reason, and frees the lock
 *    it took on the way in. A refusal always renders a sentence.
 * 3. **The control plane is rate-limited on purpose.** A pointer moves at
 *    display rate; the backend drops anything over 5 frames/s and the RC holds
 *    the last setpoint for its ttl anyway. Setpoints are therefore coalesced to
 *    one every `SETPOINT_MIN_INTERVAL_MS` and re-sent at that cadence to renew
 *    the ttl — `hover` alone is never throttled, because STOP is not a frame you
 *    schedule.
 */

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

/**
 * `IDLE → REQUESTED → LIVE ⇄ HOVER → RELEASING → IDLE`, plus the three the
 * operator does not choose.
 *
 * `HOVER` is a held-authority state, not a resting one: the session is alive,
 * the heartbeat is running and the aircraft is station-keeping under our
 * authority. `IDLE` is the state where the RC has the aircraft back.
 */
export type ManualAuthorityState =
  | 'UNAVAILABLE'
  | 'IDLE'
  | 'REQUESTED'
  | 'LIVE'
  | 'HOVER'
  | 'RELEASING'
  | 'OVERRIDE'
  | 'FAULT';

/** Every state carries a word, never only a colour (WCAG 1.4.1, and 02:00). */
export const MANUAL_STATE_LABEL: Record<ManualAuthorityState, string> = {
  UNAVAILABLE: 'UNAVAILABLE',
  IDLE: 'IDLE',
  REQUESTED: 'REQUESTING',
  LIVE: 'LIVE',
  HOVER: 'HOVER',
  RELEASING: 'RELEASING',
  OVERRIDE: 'OVERRIDE',
  FAULT: 'FAULT',
};

/** The contract's `control_release` reasons. */
export type ManualReleaseReason = 'operator' | 'timeout' | 'override' | 'fault';

/** Authority is genuinely held only in these two. */
const HOLDING: ReadonlySet<ManualAuthorityState> = new Set<ManualAuthorityState>(['LIVE', 'HOVER']);

// ---------------------------------------------------------------------------
// Timings this hook owns
// ---------------------------------------------------------------------------

/**
 * Minimum gap between two frames on the velocity plane — a `set_velocity` AND
 * a `hover`, because a stop is a frame on that plane too.
 *
 * Bounded above by the contract's 5 frames/s ceiling and below by the setpoint
 * ttl: at 320 ms the stream is 3.1 frames/s, and adding the 1 Hz heartbeat lands
 * at 4.1 — under the ceiling with room for the jitter a browser timer has, and
 * comfortably inside the 400 ms `TTL_HOLD_MS` so a held vector is renewed before
 * the RC zeroes it. Raising it past 400 would make a steady drag stutter;
 * lowering it toward 200 would spend the whole rate budget on frames the RC
 * cannot act on any faster than its 10 Hz loop.
 *
 * The ceiling only holds if EVERY frame is inside this gate. A stop used to
 * skip it — every pointerup of every gesture calls `hover()`, so an operator
 * trimming position with short nudges issued one unthrottled POST per release,
 * and at 8-10 nudges/s the per-sender bucket (5/s, burst 10) drained in about
 * two seconds. That bucket is shared by `set_velocity`, `hover` and `heartbeat`
 * alike, so it took the whole session down with it: two 429'd setpoints in a row
 * exceed `TTL_HOLD_MS` and the aircraft stutters to a stop with the operator's
 * hand still on the control, and three 429'd heartbeats reach
 * `MANUAL_HEARTBEAT_REFUSAL_LIMIT` and hand the aircraft back mid-flight. A stop
 * inside the window is parked and sent by the same timer instead — at worst
 * 320 ms after the frame it replaces, which is inside the 400 ms the RC would
 * zero that frame at anyway.
 */
const SETPOINT_MIN_INTERVAL_MS = 320;

/**
 * How long a `set_velocity` may take before it is abandoned.
 *
 * A setpoint is the one frame in this file that is REPLACED on a fixed cadence:
 * `scheduleFlush` re-arms itself after issuing the post and does not await it,
 * so a stalled TCP path — the degraded field link this whole feature exists for
 * — used to queue a new frame every 320 ms for ever, none of them ever
 * resolving, none of their handlers ever running. Bounded at the interval, at
 * most one is in flight at a time and the one that gave up is superseded by the
 * one that replaced it, which is the same thing a 429 means here.
 */
export const MANUAL_SETPOINT_TIMEOUT_MS = SETPOINT_MIN_INTERVAL_MS;

/**
 * How long a `heartbeat` may take before it is abandoned.
 *
 * Inside its own 1 Hz interval, so a stalled renewal is a MISS that the refusal
 * budget can count rather than a request that hangs silently while the COP keeps
 * rendering LIVE over a lock nothing is renewing. Not so tight that ordinary
 * latency spends a strike: the request gate refuses manual control above 1000 ms
 * round trip, and 800 leaves the whole of the range this feature will fly in.
 */
export const MANUAL_HEARTBEAT_REQUEST_TIMEOUT_MS = 800;

/**
 * A request this console gave up on, rather than one the API refused.
 *
 * Duck-typed rather than `instanceof DOMException`: `AbortSignal.timeout` throws
 * a `TimeoutError` and an explicit abort an `AbortError`, and which class those
 * arrive as differs between the browser, jsdom and undici. The name is the part
 * the platform actually guarantees.
 */
function isAbandonedRequest(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const name = (err as { name?: unknown }).name;
  return name === 'TimeoutError' || name === 'AbortError';
}

/**
 * How long a release waits for its own stop frame before handing back anyway.
 *
 * The two used to be fired together and unawaited, which let the server process
 * them in either order: a hand-back that landed first turned the stop into a 409
 * whose error path toasted "no manual control session" and overwrote the reason
 * of a release that had actually succeeded. Ordering them fixes that — but a
 * stop carries no request timeout of its own (nothing re-sends one, so bounding
 * it would be abandoning the stop itself), and a stop that hangs must never be
 * able to hold the hand-back with it. A stuck release is the worse failure of
 * the two: the authority lock then sits until the RC's 10 s dead-man, and the
 * operator cannot command the aircraft at all. Past this the release goes
 * regardless, and the RC zeroes the sticks on a release of its own accord.
 */
const HOVER_BEFORE_RELEASE_MAX_MS = 750;

/**
 * How long the hand-back itself may take before this console stops waiting.
 *
 * The courtesy stop above it has always been bounded; the release was not, and
 * a release that never settles is worse than one that fails. `release()` is
 * awaited by `dispatchManualFlyTo`, which is awaited inside the try/finally that
 * clears the FLY TO HERE strip — so a wedged nginx worker or a half-open TCP
 * path (the exact condition the setpoint bound was added for) left the strip
 * "sending" and CONFIRM disabled for the life of the page, with every later
 * FLY TO HERE turned away at the in-flight guard.
 *
 * Generous on purpose: the request gate refuses manual control above 1000 ms
 * round trip, so three seconds is a path that is not answering rather than one
 * that is slow. Past it the hand-back is reported as UNDELIVERED, which is the
 * truth — the RC keeps authority until its own 10 s dead-man expires, and that
 * dead-man is the real backstop here exactly as it is for the stop above.
 */
export const MANUAL_RELEASE_TIMEOUT_MS = 3000;

/** Resolve when `promise` settles or after `ms`, whichever comes first. */
function settledWithin(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    void promise.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      () => {
        clearTimeout(timer);
        resolve();
      },
    );
  });
}

/**
 * No grant and no denial. Not in the contract's refusal table — the table covers
 * conditions somebody can name, and this is the absence of an answer — but the
 * operator is owed a sentence either way, and "probably worked" is not one.
 */
export const MANUAL_NO_ANSWER_REASON =
  'No answer from the RC in 3s — manual control was not granted.';

/** ARM/DISARM are ground verbs; while the aircraft is being flown they are off. */
export const MANUAL_GROUND_VERBS_SUSPENDED =
  'Flight keys are active — ARM/DISARM are suspended while you hold manual control.';

/**
 * The operator selected a different aircraft while holding this one.
 *
 * The session is genuinely over — the hover and the release are already on the
 * wire — and this is the sentence that says so. It exists because the alternative
 * was worse than any wording: the COP used to release on the wire and go on
 * rendering LIVE, a control ring and "RC locked out" over the NEWLY selected
 * aircraft, where every drag, every key and STOP itself was a silent no-op.
 */
export const MANUAL_DRONE_CHANGED_REASON =
  'Manual session ended — you selected a different aircraft.';

/**
 * The tab is closing with authority held. Said as well as sent, because a
 * `beforeunload` release is best-effort: if the browser drops the request the
 * RC keeps authority until its own heartbeat dead-man expires.
 */
export const MANUAL_PAGE_UNLOAD_REASON =
  'Manual session ended — this page was closed or reloaded.';

/**
 * How long the RC may say nothing before the COP stops calling the session LIVE.
 *
 * The RC publishes `control_state` at 1 Hz while it holds authority, so three
 * missed frames is not jitter. Past this the banner shows the silence in words
 * and stops draining the dead-man — "LIVE" next to a dead-man reading 0.0s is
 * precisely the ambiguity that strip exists to remove.
 */
export const MANUAL_RC_SILENT_MS = 3000;

/** Seconds, for the sentences below — the contract's dead-man in words. */
const HEARTBEAT_TIMEOUT_S = Math.round(MANUAL_LIMITS.HEARTBEAT_TIMEOUT_MS / 1000);

/**
 * How many heartbeats in a row the API may refuse before this console gives the
 * aircraft back rather than claiming a lease nothing is renewing.
 *
 * A heartbeat is NOT a setpoint. A dropped setpoint is fixed by the next one
 * 320 ms later; a dropped heartbeat is a missed lock renewal, and the RC's
 * dead-man hands the aircraft back `HEARTBEAT_TIMEOUT_MS` after the last one
 * that landed. At 1 Hz, three refusals is 3 s of that 10 s budget — early enough
 * that the release below still has a live lock to release, instead of the COP
 * discovering at 10 s that the RC has already taken the aircraft back.
 */
export const MANUAL_HEARTBEAT_REFUSAL_LIMIT = 3;

/** Said while the session is still recoverable — the operator is owed the warning. */
export function manualHeartbeatRefused(consecutive: number, detail?: string | null): string {
  const why = detail?.trim() ? ` ${detail.trim()}` : '';
  return `${consecutive} heartbeat${consecutive === 1 ? '' : 's'} in a row refused by the API — the authority lock is not being renewed, and the RC takes the aircraft back ${HEARTBEAT_TIMEOUT_S}s after the last renewal.${why}`;
}

/**
 * The hand-back did not land, however the session was ending.
 *
 * Said by the explicit `release` and by the fire-and-forget teardown alike — a
 * drone switch or a dead-man that fails to hand back leaves exactly the same
 * aircraft in exactly the same state as a failed Escape, and used to say
 * nothing at all.
 */
export const MANUAL_RELEASE_UNDELIVERED_REASON =
  'Release could not be delivered — the RC keeps authority until its heartbeat dead-man expires.';

/**
 * Said when the heartbeat was abandoned rather than answered.
 *
 * A refusal and a timeout are different problems: one is the API saying no, the
 * other is the path to it not answering at all. Both spend a strike, and the
 * operator is told which they are looking at.
 */
export const MANUAL_HEARTBEAT_TIMEOUT_DETAIL =
  `No answer within ${MANUAL_HEARTBEAT_REQUEST_TIMEOUT_MS}ms — the request was abandoned, not refused.`;

/**
 * Said when the heartbeat never reached the API at all.
 *
 * `api.post` turns an ANSWER into an `ApiRequestError`; a connection that failed
 * before there was one — an LTE handover, a Wi-Fi roam, DNS, a dropped TLS
 * handshake — comes back as a raw `TypeError`. That is the degraded field link
 * this whole feature exists for, and it is no more evidence about the session
 * than a request that ran out of time: the RC is still flying it and its own
 * dead-man is 10 s. So it spends a strike like any other missed renewal instead
 * of ending the session where it stands, and it says which of the two it was —
 * a refusal and a link that dropped are different problems.
 */
export const MANUAL_HEARTBEAT_TRANSPORT_DETAIL =
  'The request never reached the API — the connection dropped, it was not refused.';

/** Said when it is not. */
export const MANUAL_HEARTBEAT_LOST_REASON =
  `Manual session ended — ${MANUAL_HEARTBEAT_REFUSAL_LIMIT} heartbeats in a row were refused by the API, so nothing was renewing the authority lock.`;

/**
 * The COP's own dead-man: the RC has stopped reporting for as long as its
 * heartbeat timeout.
 *
 * The RC publishes `control_state` at 1 Hz for as long as it holds authority, so
 * silence this long means our frames are not reaching it either — its own
 * dead-man has zeroed the aircraft and given the sticks back. Without this the
 * COP renews the backend lock for ever while every drag is a no-op, which is a
 * green banner over an aircraft nobody is flying.
 */
export const MANUAL_RC_LOST_REASON =
  `Manual session ended — the RC has not reported for ${HEARTBEAT_TIMEOUT_S}s. Its own dead-man has stopped the aircraft, so nothing here was reaching it.`;

/** How often the two dead-men above are evaluated. Same 1 Hz as the heartbeat. */
const DEADMAN_TICK_MS = MANUAL_LIMITS.HEARTBEAT_INTERVAL_MS;

/**
 * A session this console already held, picked back up after a reload.
 *
 * Not a refusal and not a warning — it is the sentence that explains why the COP
 * came back holding an aircraft the operator does not remember asking for on
 * this page load.
 */
export const MANUAL_REHYDRATED_REASON =
  'Manual session resumed after a reload — this console still holds the aircraft.';

/**
 * Another screen is flying the session this one thinks it holds.
 *
 * Proved rather than guessed: the RC advances `seq_ack` only to a seq it
 * ACCEPTED, and it accepts nothing this console did not send — so a `seq_ack`
 * past our own highest seq is somebody else's frames. It happens through
 * rehydration, where the three adoption conditions (the holder is this account,
 * the RC is still live, `seq_ack` is known) are ALL satisfied by a session
 * another tab on the same account is actively driving.
 *
 * Ending it locally is the only honest answer, and it is deliberately local:
 * a `release` names the session, the backend honours it from the account that
 * holds it, and this console would therefore end a colleague's live flight from
 * a tab that was never flying it.
 */
export const MANUAL_FOREIGN_SESSION_REASON =
  'Manual session ended here — another screen is flying it. The RC has accepted frames this console never sent, so nothing done here was reaching the aircraft.';

/** The RC states in which a session is still worth resuming. */
const RESUMABLE_AUTHORITY: ReadonlySet<string> = new Set(['live', 'hover']);

/**
 * The sentences that describe a session ENDING rather than a request being
 * refused.
 *
 * They all reach the same strip once the controller is back in IDLE, and
 * labelling them "Manual refused · CALLSIGN" would be a claim about an aircraft
 * nobody was refused — usually about whichever aircraft is selected NOW.
 */
export const MANUAL_SESSION_ENDED_REASONS: ReadonlySet<string> = new Set([
  MANUAL_DRONE_CHANGED_REASON,
  MANUAL_PAGE_UNLOAD_REASON,
  MANUAL_HEARTBEAT_LOST_REASON,
  MANUAL_RC_LOST_REASON,
  MANUAL_FOREIGN_SESSION_REASON,
]);

// ---------------------------------------------------------------------------
// Public shapes
// ---------------------------------------------------------------------------

/** One commanded frame, in the contract's normalised axes. */
export interface ManualFrame {
  /** North (frame `map`), −1..1. */
  vx: number;
  /** East (frame `map`), −1..1. */
  vy: number;
  /** Up positive, −1..1. */
  vz: number;
  /** Clockwise positive, −1..1. */
  yawRate: number;
  frame?: ControlFrame;
  /** Defaults to `TTL_HOLD_MS`; pass `TTL_NUDGE_MS` for a discrete nudge. */
  ttlMs?: number;
}

/**
 * Which flight keys are down right now, as normalised axes.
 *
 * Held here rather than in the control surface because the keyboard is global —
 * the operator's focus is wherever they last clicked — and because this mapping
 * IS the complete non-pointer path to every gesture (WCAG 2.1.1, Keyboard). The
 * surface turns these into a setpoint, since only it knows the ring.
 *
 * It is not a 2.5.7 (Dragging Movements) alternative and is not claimed as one:
 * 2.5.7 asks for a SINGLE-POINTER path that is not a drag, and a keyboard is a
 * different input. The vector drag rests on 2.5.7's "essential" exception
 * instead — it commands a continuously variable velocity, and a tap-and-step
 * equivalent would be a different control commanding a different thing. See the
 * header of `ManualMapControl` for the same reasoning where the gesture lives.
 */
export interface ManualKeyAxes {
  /** W = +1 forward, S = −1 back. */
  forward: number;
  /** D = +1 right, A = −1 left. */
  right: number;
  /** C = +1 up, Z = −1 down. */
  up: number;
  /** E = +1 clockwise, Q = −1 counter-clockwise. */
  yaw: number;
}

const NO_AXES: ManualKeyAxes = { forward: 0, right: 0, up: 0, yaw: 0 };

/**
 * The DJI mapping, which is what an operator who has ever flown a Mavic already
 * has in their hands. Deliberately not remapped to something "clearer": muscle
 * memory is the whole argument for a fixed key layout.
 */
const AXIS_KEYS: Record<string, { axis: keyof ManualKeyAxes; value: 1 | -1 }> = {
  w: { axis: 'forward', value: 1 },
  s: { axis: 'forward', value: -1 },
  d: { axis: 'right', value: 1 },
  a: { axis: 'right', value: -1 },
  c: { axis: 'up', value: 1 },
  z: { axis: 'up', value: -1 },
  e: { axis: 'yaw', value: 1 },
  q: { axis: 'yaw', value: -1 },
};

export interface ManualControlController {
  state: ManualAuthorityState;
  /** `MANUAL_STATE_LABEL[state]` — a word for every state. */
  label: string;
  /** Why the state is what it is: a refusal, a warning, or null. */
  reason: string | null;
  /** True while `available` is false and nothing is held. */
  unavailableReason: string | null;
  sessionId: string | null;
  /** The limits the RC was told to enforce. Null until a request is accepted. */
  envelope: ManualEnvelope | null;
  /** Last measured command round trip, ms. Null before the first measurement. */
  rttMs: number | null;
  /** Setpoint dead-man remaining as the RC last reported it. Null = not reported. */
  ttlRemainingMs: number | null;
  /**
   * Epoch ms this browser received the RC's last `control_state` frame, or null
   * before the first one.
   *
   * Measured on OUR clock, not read from the frame's `received_at`: it is
   * compared against `Date.now()` to decide whether the RC has gone quiet, and a
   * server timestamp in that subtraction would put the skew between two machines
   * into a safety readout. What the operator is being told is "how long since
   * this screen last heard anything", which is exactly what this measures.
   */
  lastStateAtMs: number | null;
  /** Envelope limits the RC says it is clamping against, in the contract's words. */
  clamped: ManualClampReason[];
  clampDetail: string | null;
  pilotOverride: boolean;
  /** Epoch ms the operator took authority. Null when nothing is held. */
  heldSince: number | null;
  holdsAuthority: boolean;
  /** A request or a release is in flight. */
  busy: boolean;
  keyAxes: ManualKeyAxes;
  /** Operator speed preset, m/s — a local cap, never a change to the envelope. */
  speedCapMs: number;
  setSpeedCapMs: (ms: number) => void;
  yawRateDps: number;
  request: () => void;
  /**
   * Hand authority back, and say whether it actually landed.
   *
   * Resolves TRUE when this console no longer holds the aircraft — the release
   * reached the API, or there was nothing held to begin with. FALSE means the
   * release could not be delivered, so the RC keeps authority until its own
   * dead-man expires and anything that needs the aircraft free (a `goto`, which
   * the RC refuses while a manual session is up) must not be sent.
   */
  release: (reason?: ManualReleaseReason) => Promise<boolean>;
  /** Coalesced. All-zero axes become an explicit `hover`. */
  send: (frame: ManualFrame) => void;
  /** Immediate, unthrottled, unconditional while authority is held. */
  hover: () => void;
  /**
   * Bumped by every `hover()`, so a control surface can DISARM what it is
   * holding.
   *
   * STOP has to reach the gestures, not only the wire. `hover()` clears the
   * flight keys itself, but a pointer drag lives in the surface — and a drag
   * that survives the stop is re-sent by the surface's own send effect on the
   * next pixel of movement, which is full commanded flight resuming from a
   * hand that never made a new gesture. Keyboard disarmed and pointer not is
   * the asymmetry that makes it a surprise, and the panic path is the worst
   * place to have one.
   *
   * A counter rather than a boolean because two stops in a row are two events
   * and must both be observable; monotonic for the same reason the session
   * epoch is.
   */
  stopSignal: number;
  /**
   * Offer a keydown to the flight keys. Returns true when consumed, so the
   * command bar's existing listener can stop rather than also firing a verb.
   */
  handleKeyDown: (event: KeyboardEvent) => boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface Session {
  id: string;
  /** The `control_request` command id — how the RC's grant/deny ack is matched. */
  commandId: string;
  seq: number;
  /**
   * True when this session was picked up from a snapshot rather than granted to
   * this console.
   *
   * The only kind that another live console can be flying at the same time —
   * session ids are minted per `request`, so a granted one is nobody else's to
   * drive. It is what gates the `seq_ack` check in the `control_state`
   * subscriber, which must not be able to end a session this console was
   * actually granted.
   */
  adopted: boolean;
}

export function useManualControl({
  droneId,
  available,
  unavailableReason = null,
  subscribeManualControlState,
  subscribeDroneAck,
  yawRateDps = MANUAL_LIMITS.MAX_YAW_RATE_DPS,
  layout = 'map',
  rehydrate = null,
  selfUserId = null,
}: {
  /** Null when nothing is selected — every verb becomes a no-op. */
  droneId: string | null;
  /** From `manualControlOffer`. False blocks `request`, never a release. */
  available: boolean;
  unavailableReason?: string | null;
  subscribeManualControlState: (cb: (s: ManualControlStatePayload) => void) => () => void;
  subscribeDroneAck: (cb: (a: DroneAckPayload) => void) => () => void;
  /** The operator's yaw-rate choice; the schema admits only 30 or 60. */
  yawRateDps?: number;
  /** Which COP layout the operator flew in. Audit only — see the contract. */
  layout?: string;
  /**
   * `manual_control` from `GET /api/drones/:id` — the live session on THIS
   * aircraft as the API last reported it. Null while unknown or when nobody is
   * flying it by hand. See the adoption effect for what is and is not resumed.
   */
  rehydrate?: ManualControlSnapshot | null;
  /**
   * The signed-in user's id. A session is only ever picked back up when it is
   * THEIRS — an operator must never inherit somebody else's authority from a
   * page load.
   */
  selfUserId?: string | null;
}): ManualControlController {
  const [state, setState] = useState<ManualAuthorityState>('IDLE');
  const [reason, setReason] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [envelope, setEnvelope] = useState<ManualEnvelope | null>(null);
  const [rttMs, setRttMs] = useState<number | null>(null);
  const [ttlRemainingMs, setTtlRemainingMs] = useState<number | null>(null);
  const [lastStateAtMs, setLastStateAtMs] = useState<number | null>(null);
  const [clamped, setClamped] = useState<ManualClampReason[]>([]);
  const [clampDetail, setClampDetail] = useState<string | null>(null);
  const [pilotOverride, setPilotOverride] = useState(false);
  const [heldSince, setHeldSince] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [keyAxes, setKeyAxes] = useState<ManualKeyAxes>(NO_AXES);
  const [stopSignal, setStopSignal] = useState(0);
  const [speedCapMs, setSpeedCapMs] = useState<number>(MANUAL_LIMITS.MAX_SPEED_MS);

  // Everything a timer, a global listener or an unmount cleanup reads goes
  // through a ref. Those callers all outlive the render that created them, and a
  // dead-man built on a stale closure is a dead-man that does not fire.
  const sessionRef = useRef<Session | null>(null);
  /**
   * Monotonic SESSION EPOCH — bumped on every session start and every session
   * end (see `setSession`, which is the only writer of `sessionRef`).
   *
   * Nothing here can cancel a request that is already on the wire: `clearFlush`
   * and `stopHeartbeat` cancel TIMERS. So a heartbeat for the aircraft the
   * operator just left can still be in flight when they take the next one, and
   * it comes back a second later carrying a 409 for a session that is long over.
   * Acted on, it ends whatever session is live NOW — a different aircraft's,
   * with no hover and no release, leaving that lock un-renewed until its TTL
   * while the console tells the operator an aircraft that was fine refused them.
   *
   * Every send stamps this counter, and every completion path compares before
   * it touches state. A reply whose epoch has moved on is not an error to
   * report; it is a message about an aircraft nobody is flying any more.
   *
   * An epoch rather than the session id: two sessions can share an id shape,
   * and only a counter also separates IDLE from a later IDLE.
   */
  const epochRef = useRef(0);
  const droneIdRef = useRef(droneId);
  droneIdRef.current = droneId;
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const stateRef = useRef(state);
  stateRef.current = state;
  /**
   * So `request` can enforce the `available` contract its own prop doc promises.
   *
   * It did not, for a long time: `available` only picked the UNAVAILABLE label,
   * and the refusal was enforced entirely by the `disabled` attribute on the two
   * buttons that call `request`. That held only for as long as nobody added a
   * third caller. It matters more now that manual flight ships switched OFF
   * (`MANUAL_FLIGHT_RELEASED`), because a bypass here is not a mis-rendered key -
   * it is an unreleased flight capability taking authority over a real aircraft.
   *
   * A ref rather than a dep so that `request`'s identity does not change on every
   * telemetry frame; it is read at press time, which is the only moment it means
   * anything.
   */
  const availableRef = useRef(available);
  availableRef.current = available;
  const mountedRef = useRef(true);

  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const grantTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<ManualFrame | null>(null);
  const lastSentAtRef = useRef(0);
  /**
   * A stop that arrived inside the cadence window, waiting for the flush timer.
   *
   * It rides the SAME timer as a pending vector and takes precedence over one:
   * the operator let go, so whatever was queued to fly is not what they are
   * asking for. Nothing renews it — a stop is sent once, and the RC's 400 ms
   * `TTL_HOLD_MS` on the frame before it is what stops the aircraft if it does
   * not land.
   */
  const pendingStopRef = useRef(false);
  /**
   * A stop is the last thing this console has put on (or parked for) the wire,
   * and nothing has been flown since.
   *
   * Makes a repeated stop a no-op — the same rule `send` already applies to a
   * zero frame. `endGesture` hovers on every pointerup and pointercancel of all
   * four gestures, so without this a flurry of releases is a POST each, all
   * saying the identical thing to an aircraft that is already stopped.
   */
  const stopStandsRef = useRef(false);
  const heldKeysRef = useRef<Set<string>>(new Set());
  /** Consecutive heartbeats the API has refused. Reset by every one that lands. */
  const heartbeatRefusalsRef = useRef(0);
  /** The exact warning those refusals put on screen, so only it is cleared again. */
  const heartbeatWarningRef = useRef<string | null>(null);
  /**
   * The last moment there was any evidence the RC is still on the other end:
   * the grant, a `control_state` frame, or the snapshot a reload resumed from.
   *
   * Separate from `lastStateAtMs`, which is only ever a real frame and is what
   * the banner reports. This one is the COP's own dead-man baseline, so it may
   * legitimately be set by a grant that no frame has followed yet — otherwise
   * either the countdown would be a fiction or the dead-man would fire on a
   * session that is one second old.
   */
  const rcSignalAtRef = useRef<number | null>(null);
  /** Session ids already resumed, so a re-render cannot adopt one twice. */
  const adoptedRef = useRef<Set<string>>(new Set());
  /**
   * The `seq_ack` of the last frame that was AHEAD of everything this console
   * has sent, or null when the last frame was not ahead. Two such frames, each
   * further ahead than the last, is what proves a second sender — see
   * `MANUAL_FOREIGN_SESSION_REASON` and the check that reads this.
   */
  const seqAheadRef = useRef<number | null>(null);

  const holdsAuthority = HOLDING.has(state);
  const holdsAuthorityRef = useRef(holdsAuthority);
  holdsAuthorityRef.current = holdsAuthority;
  /**
   * Something of this console's is on the aircraft: authority held, or a request
   * whose POST has returned and whose grant has not.
   *
   * The wider of the two conditions, for the teardown paths — a lock taken by a
   * `request` that is still waiting on the RC has to be handed back too.
   */
  const engaged = holdsAuthority || state === 'REQUESTED';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * The ONLY way `sessionRef` is written, so no session can start or end
   * without the epoch moving. Returns the epoch the caller now owns, which is
   * what a hand-back compares against before writing state seconds later.
   */
  const setSession = useCallback((next: Session | null): number => {
    sessionRef.current = next;
    // A session that is starting has told the RC nothing yet, so its first stop
    // is never a repeat of one already standing; one that is ending must not
    // leave the next session believing a stop of its own is in force.
    stopStandsRef.current = false;
    epochRef.current += 1;
    return epochRef.current;
  }, []);

  // -------------------------------------------------------------------------
  // Transport
  // -------------------------------------------------------------------------

  const endpoint = useCallback(
    (id: string) => `/api/drones/${id}/manual-control`,
    [],
  );

  /**
   * POST one action and time it.
   *
   * Every call feeds `rttMs`, which is the only latency figure this system has:
   * nothing on the server can time the RC's radio link, so the browser measures
   * its own round trip and the backend gates on what it reports. That makes this
   * measurement operator-facing data, not instrumentation — the banner shows it
   * live, and a link degrading mid-session is exactly what an operator needs to
   * see before it becomes a surprise.
   *
   * `timeoutMs` bounds the call. A frame that runs out of time is NOT measured:
   * its elapsed time is the bound, not the link, so writing it would pin the
   * operator-facing latency figure at exactly the timeout and hold it there for
   * as long as the link stays broken — a link getting steadily worse would read
   * as one holding perfectly steady at 320 ms. The last figure that was actually
   * measured is left standing instead, and what the operator watches for the
   * link failing is the refusal warning and the dead-man, both of which say it
   * in words.
   */
  const post = useCallback(
    async <T,>(
      body: Record<string, unknown>,
      options: { timeoutMs?: number } = {},
    ): Promise<T | null> => {
      const id = droneIdRef.current;
      if (!id) return null;
      // Stamped at SEND time. A round trip that resolves after its session is
      // over measured a link to an aircraft the operator has left, and the
      // banner it would repaint belongs to the one they are flying now.
      const epoch = epochRef.current;
      const startedAt = Date.now();
      const measure = () => {
        if (mountedRef.current && epochRef.current === epoch) setRttMs(Date.now() - startedAt);
      };
      try {
        const res = await api.post<{ data: T }>(endpoint(id), body, options);
        measure();
        return res.data;
      } catch (err) {
        if (!isAbandonedRequest(err)) measure();
        throw err;
      }
    },
    [endpoint],
  );

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current !== null) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const clearGrantTimer = useCallback(() => {
    if (grantTimerRef.current !== null) {
      clearTimeout(grantTimerRef.current);
      grantTimerRef.current = null;
    }
  }, []);

  const clearFlush = useCallback(() => {
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    pendingRef.current = null;
    // Nothing is queued for the wire any more, a parked stop included. Every
    // caller that clears the flush either sends its own stop (`release`,
    // `abandonSession`) or is ending the session outright.
    pendingStopRef.current = false;
  }, []);

  /**
   * A teardown frame did not land. Say so — to the console log always, and to
   * the operator on the paths where there is still a screen to say it on.
   *
   * Both used to end in `.catch(() => undefined)`. That is the shape of
   * swallowing that hid the original rate-limit death: this is the teardown for
   * a drone switch, an unmount, a page unload and BOTH dead-men, and the exit
   * dialog tells the operator "leaving hands the aircraft back to the RC" on the
   * way in. A hand-back that fails after that promise is the one failure in this
   * file that must never be silent.
   */
  const noteHandbackFailure = useCallback(
    (id: string, session: Session, action: 'hover' | 'release', err: unknown) => {
      console.error('[manual] hand-back frame failed', {
        droneId: id,
        sessionId: session.id,
        action,
        status: err instanceof ApiRequestError ? err.status : null,
        detail: err instanceof Error ? err.message : String(err),
      });
    },
    [],
  );

  /**
   * Teardown for every path that cannot await a render: the operator selecting a
   * different aircraft, unmount, both dead-men, and the page unloading.
   *
   * The stop goes first, and on every path but the unload it goes first ON THE
   * WIRE — the release is chained off the stop's settlement rather than fired
   * beside it, because two independent `fetch`es arrive in whichever order the
   * network gives them and an aircraft must never be handed back mid-vector.
   * Bounded by `HOVER_BEFORE_RELEASE_MAX_MS` for the same reason `release` is: a
   * stalled stop must not be able to hold the hand-back with it, and a lock left
   * held is the worse of the two failures.
   *
   * `unload: true` is the one case that cannot chain — the document is going
   * away and there is no later tick to send from — so there both frames are
   * fired together and the ordering is genuinely best-effort.
   */
  const abandonSession = useCallback(
    (
      id: string,
      session: Session,
      why: ManualReleaseReason,
      options: { unload?: boolean } = {},
    ) => {
      const url = `/api/drones/${id}/manual-control`;
      // Everything below resolves after this call returns, and by then the
      // operator can be flying ANOTHER aircraft. Anything said on screen is
      // gated on this epoch — the callers all tear the session down before
      // getting here, so it is the epoch of a console holding nothing.
      const epoch = epochRef.current;
      // `keepalive` because one caller is `beforeunload`: a plain fetch is
      // cancelled with the document, so the release would never leave the
      // browser and the aircraft would stay locked for the RC's full dead-man.
      // Harmless everywhere else — it only says the request may outlive the page.
      const stop = api
        .post(
          url,
          { action: 'hover', session_id: session.id, seq: (session.seq += 1) },
          { keepalive: true },
        )
        .catch((err: unknown) => noteHandbackFailure(id, session, 'hover', err));
      const handBack = () =>
        api
          .post(
            url,
            { action: 'release', session_id: session.id, reason: why, layout: layoutRef.current },
            { keepalive: true },
          )
          .catch((err: unknown) => {
            noteHandbackFailure(id, session, 'release', err);
            // "Nobody holds this aircraft" is not a failed hand-back — the same
            // answer `release` treats as success. Everything else means the RC
            // keeps authority until its own dead-man expires, which the operator
            // has to know before they try to command that aircraft again.
            const alreadyFree =
              err instanceof ApiRequestError &&
              err.status === 409 &&
              isNothingToReleaseRefusal(err.message);
            if (alreadyFree || options.unload) return;
            if (!mountedRef.current || epochRef.current !== epoch) return;
            // And never over an aircraft the operator IS flying. A hand-back
            // that failed for the one they left is worth saying; saying it on
            // the banner of the one in their hands is not.
            if (sessionRef.current !== null) return;
            setReason(getApiErrorMessage(err, MANUAL_RELEASE_UNDELIVERED_REASON));
          });
      if (options.unload) {
        void stop;
        void handBack();
        return;
      }
      void settledWithin(stop, HOVER_BEFORE_RELEASE_MAX_MS).then(handBack);
    },
    [noteHandbackFailure],
  );

  /** The session is over, whoever ended it. Never leaves a timer running. */
  const endSessionLocally = useCallback(
    (next: ManualAuthorityState, why: string | null) => {
      setSession(null);
      stopHeartbeat();
      clearGrantTimer();
      clearFlush();
      heldKeysRef.current.clear();
      heartbeatRefusalsRef.current = 0;
      heartbeatWarningRef.current = null;
      rcSignalAtRef.current = null;
      seqAheadRef.current = null;
      if (!mountedRef.current) return;
      setKeyAxes(NO_AXES);
      setSessionId(null);
      setHeldSince(null);
      setTtlRemainingMs(null);
      setLastStateAtMs(null);
      // Nothing is in flight once the session is over. Left set, it painted a
      // spinner on the MANUAL key for ever after a request that was overtaken —
      // the operator selecting another aircraft mid-request is the way that
      // happens, and the `request` continuation has no path back to it.
      setBusy(false);
      setState(next);
      setReason(why);
    },
    [clearFlush, clearGrantTimer, setSession, stopHeartbeat],
  );

  /**
   * The COP itself decided the session is over: hand the aircraft back on the
   * wire as well as on screen, and say why.
   *
   * Both dead-men below end here, and so does a frame that faulted. The release
   * is best-effort — the very thing that ended the session is usually the thing
   * that will drop it — but it is the difference between an authority lock freed
   * now and one that sits until its TTL expires.
   *
   * `next` is the state the console lands in. IDLE for a session that simply
   * ended; FAULT for one a frame error killed, which is a different thing to
   * say and the strip says it differently.
   */
  const abortSession = useCallback(
    (why: string, releaseReason: ManualReleaseReason, next: ManualAuthorityState = 'IDLE') => {
      const session = sessionRef.current;
      const id = droneIdRef.current;
      // Locally FIRST, and the order matters: `endSessionLocally` is what nulls
      // `sessionRef` and moves the epoch, so tearing down before handing back
      // means the hand-back owns the epoch of a console holding nothing — which
      // is exactly what gates whether its failure is allowed to speak.
      endSessionLocally(next, why);
      if (session && id) abandonSession(id, session, releaseReason);
      toast.error(why);
    },
    [abandonSession, endSessionLocally],
  );

  /**
   * A frame on the VELOCITY PLANE failed — a setpoint or a stop.
   *
   * Only two answers the API actually gave change anything: 409 means this
   * session no longer holds the aircraft (the lock expired, or the RC ended it)
   * and continuing to send would be pretending otherwise; everything else it
   * answers is a fault worth stopping on. A 429, and anything that never became
   * an answer at all, is one lost frame and nothing more.
   *
   * A HEARTBEAT does not go through here — see `handleHeartbeatError`. What is
   * absorbed here is absorbed because a SETPOINT is replaced 320 ms later; a
   * heartbeat has nothing behind it but the lock it was renewing, so the same
   * failures cost it a strike instead.
   */
  const handleFrameError = useCallback(
    (err: unknown, epoch: number) => {
      // The frame outlived its session. Whatever the API is refusing, it is not
      // refusing the session that is live now — and ending THAT one here is how
      // a late 409 for the aircraft the operator left takes the one in their
      // hands, silently, with no hover and no release on the wire.
      if (epochRef.current !== epoch) return;
      // Abandoned rather than refused: the frame ran out of the time budget the
      // NEXT frame is already inside. Absorbed exactly like the 429 below, and
      // for the same reason — one setpoint was lost, and the one replacing it
      // 320 ms later is the fix. It is not evidence about the session.
      if (isAbandonedRequest(err)) return;
      // Not an answer at all. `api.post` turns only a non-2xx RESPONSE into an
      // `ApiRequestError`; a connection that failed before there was one — an
      // LTE handover, a Wi-Fi roam, a dropped TLS handshake, all of them the
      // degraded field link this feature exists for — arrives as a raw
      // `TypeError`, and used to end the session on the FIRST one with a FAULT
      // toast while the RC was still happily flying it. Absorbed for the same
      // reason a timeout is: one frame was lost — replaced 320 ms later if it
      // was a setpoint, and covered by the RC's own 400 ms hold if it was a stop
      // — and it says nothing about whether the lock is held. What JUDGES the
      // link is the heartbeat, which charges every one of these a strike and
      // gives the aircraft back at three (`handleHeartbeatError`) — so a link
      // that is genuinely gone still ends the session inside three seconds,
      // with a sentence that says which failure it was.
      if (!(err instanceof ApiRequestError)) return;
      if (err.status === 429) {
        // One frame dropped by the rate ceiling. A setpoint is replaced 320 ms
        // later, so absorbing it is free; a STOP is not re-sent by anything,
        // and what stops the aircraft then is the RC zeroing the last setpoint
        // at its own 400 ms `TTL_HOLD_MS` — later than the operator asked for,
        // but not a silent failure to stop. The stop is inside the same cadence
        // gate as the setpoints now, so this is the platform's global limiter
        // refusing rather than this console's own stream overrunning.
        return;
      }
      if (err.status === 409) {
        // Local ONLY, and correctly so: a 409 is the API saying this console
        // holds nothing. There is no session left to hand back, and a release
        // for one would be a second refusal on the way out.
        endSessionLocally('IDLE', err.message);
        toast.error(err.message);
        return;
      }
      // Everything else the API answered — a 502 mid-deploy, a 500, a gateway
      // timeout — says nothing about whether the LOCK is still held, and it
      // almost always is. Ending only locally left the last setpoint in force
      // until the RC zeroed it at its 400 ms ttl and the backend lock held,
      // un-renewed, for its full 10 s TTL: the operator could neither fly the
      // aircraft nor re-take it. Hand it back on the wire, the way both dead-men
      // do.
      abortSession(err.message, 'fault', 'FAULT');
    },
    [abortSession, endSessionLocally],
  );

  /**
   * A HEARTBEAT failed, which is a missed LOCK RENEWAL and not a dropped frame.
   *
   * The manual plane has its own 5 frames/s ceiling, but the platform's global
   * limiter answers 429 for a whole window and does not care which route asked —
   * so a heartbeat can be refused for reasons that have nothing to do with this
   * session's own rate. Absorbing that silently, the way a setpoint's 429 is
   * absorbed, leaves the COP rendering LIVE over a lease nobody is renewing
   * until the RC's dead-man quietly takes the aircraft back.
   *
   * So it is surfaced immediately and ends the session at
   * `MANUAL_HEARTBEAT_REFUSAL_LIMIT`.
   *
   * TWO refusals get that three-strike budget, not one. The other is the
   * backend's RC-silence 409 (`isRcSilenceRefusal`), which fires when
   * `manual_control_state` carries no frame for this session inside
   * `RC_SILENCE_MS` — and those frames are QoS 0 at 1 Hz over a radio link, so a
   * short gap is a transient every other layer is built to ride out: the RC's own
   * dead-man is 10 s, and it keeps flying the session across exactly this. Ending
   * on the FIRST one handed the aircraft back over a two-second silence the
   * aircraft itself had already survived. It is counted rather than ignored,
   * because a lock nothing renews is still a lock nothing renews.
   *
   * Matched on the refusal SENTENCE, never on the bare status: `NO_SESSION` is
   * a 409 too and it means this console's authority is genuinely gone, which
   * must stay terminal on the first occurrence.
   */
  const handleHeartbeatError = useCallback(
    (err: unknown, epoch: number) => {
      // Same reason as `handleFrameError`, plus one of its own: the refusal
      // budget below is a single counter shared by every session this hook ever
      // holds, so charging it for a dead session's refusal spends the live
      // session's three strikes on an aircraft nobody is flying.
      if (epochRef.current !== epoch) return;
      // A heartbeat that ran out of time is a MISSED RENEWAL, and it must cost a
      // strike rather than vanish: falling through to `handleFrameError` would
      // absorb it exactly like a superseded setpoint (there is nothing behind a
      // heartbeat to supersede it) and the COP would render LIVE over a lock
      // nothing is renewing — the very failure the bound was added to expose.
      const abandoned = isAbandonedRequest(err);
      // Nothing answered at all: the connection failed before the API was
      // reached. An LTE handover or a Wi-Fi roam on the operator's tablet fails
      // one heartbeat FAST, so it arrives here as a `TypeError` rather than as
      // the 800 ms timeout the identical failure produces on a stalled path —
      // and it used to fall through to `handleFrameError` and end the session on
      // the first occurrence, while the same gap arriving one way got three
      // strikes and the same gap arriving the other got none. It is a missed
      // renewal either way, and the RC rides out far more than one.
      const unreachable = !abandoned && !(err instanceof ApiRequestError);
      const tolerable =
        abandoned ||
        unreachable ||
        (err instanceof ApiRequestError &&
          (err.status === 429 || (err.status === 409 && isRcSilenceRefusal(err.message))));
      if (!tolerable) {
        handleFrameError(err, epoch);
        return;
      }
      const consecutive = (heartbeatRefusalsRef.current += 1);
      if (consecutive >= MANUAL_HEARTBEAT_REFUSAL_LIMIT) {
        abortSession(MANUAL_HEARTBEAT_LOST_REASON, 'timeout');
        return;
      }
      // The server's own sentence rides along: "the RC is not reporting this
      // session" and "you are being rate limited" are different problems with
      // different answers, and the count alone does not tell them apart.
      const warning = manualHeartbeatRefused(
        consecutive,
        abandoned
          ? MANUAL_HEARTBEAT_TIMEOUT_DETAIL
          : unreachable
            ? MANUAL_HEARTBEAT_TRANSPORT_DETAIL
            : err instanceof ApiRequestError && err.status === 409
              ? err.message
              : null,
      );
      heartbeatWarningRef.current = warning;
      if (mountedRef.current) setReason(warning);
    },
    [abortSession, handleFrameError],
  );

  /** A heartbeat landed: the lock is renewed, and any warning about it is stale. */
  const noteHeartbeatDelivered = useCallback((epoch: number) => {
    // A heartbeat that lands after its session ended renewed nothing that is
    // still held, and the counter it would clear is the live session's.
    if (epochRef.current !== epoch) return;
    if (heartbeatRefusalsRef.current === 0) return;
    heartbeatRefusalsRef.current = 0;
    const warning = heartbeatWarningRef.current;
    heartbeatWarningRef.current = null;
    if (!mountedRef.current || warning === null) return;
    // Only ever clears ITS OWN sentence. A clamp detail or a server warning
    // that replaced it in the same field belongs to something else entirely.
    setReason((prev) => (prev === warning ? null : prev));
  }, []);

  const nextSeq = useCallback((session: Session): number => {
    session.seq += 1;
    return session.seq;
  }, []);

  // `scheduleFlush` re-enters itself to renew a held setpoint, and a parked stop
  // rides the same timer. Declaring the ref first keeps that from needing a
  // mutually recursive pair of callbacks.
  const scheduleFlushRef = useRef<() => void>(() => {});

  /**
   * Put a stop on the wire NOW, and start the cadence window from it.
   *
   * Deliberately unbounded, unlike a setpoint: nothing re-sends a stop, so
   * abandoning one would be abandoning the stop itself.
   */
  const postStopFrame = useCallback(
    (session: Session) => {
      lastSentAtRef.current = Date.now();
      const epoch = epochRef.current;
      void post({ action: 'hover', session_id: session.id, seq: nextSeq(session) }).catch((err) =>
        handleFrameError(err, epoch),
      );
    },
    [handleFrameError, nextSeq, post],
  );

  /**
   * STOP.
   *
   * Immediate whenever the cadence allows it, which for a real stop is almost
   * always: the operator has to have sent a frame inside the last 320 ms for
   * this to defer at all, and then the deferral is only to the end of that same
   * window. What it will NOT do is bypass the rate gate — see
   * `SETPOINT_MIN_INTERVAL_MS` for the failure that cost, and note that the
   * bucket a burst of stops drains is the one the heartbeat renews the authority
   * lock through.
   *
   * The release does NOT go through here: it has to order its stop against its
   * own hand-back and swallow that stop's failure, which is the opposite of what
   * every caller here wants. See `release`.
   */
  const sendHoverNow = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    if (mountedRef.current) setState((prev) => (prev === 'LIVE' ? 'HOVER' : prev));
    // Already stopped, with nothing flown since. Repeating it on the wire tells
    // the RC nothing it has not been told, and `send` treats a repeated zero
    // frame exactly this way.
    if (stopStandsRef.current) return;
    // Whatever vector was queued is not what the operator is asking for now.
    clearFlush();
    stopStandsRef.current = true;
    if (Date.now() < lastSentAtRef.current + SETPOINT_MIN_INTERVAL_MS) {
      pendingStopRef.current = true;
      scheduleFlushRef.current();
      return;
    }
    postStopFrame(session);
  }, [clearFlush, postStopFrame]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current !== null) return;
    const waitMs = Math.max(0, lastSentAtRef.current + SETPOINT_MIN_INTERVAL_MS - Date.now());
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      const session = sessionRef.current;
      if (!session) return;
      // A stop parked inside the window goes first and goes ALONE: it already
      // dropped the vector that was pending, and nothing renews a stop, so this
      // timer is not re-armed behind it.
      if (pendingStopRef.current) {
        pendingStopRef.current = false;
        postStopFrame(session);
        return;
      }
      const pending = pendingRef.current;
      if (!pending) return;
      const epoch = epochRef.current;
      lastSentAtRef.current = Date.now();
      void post(
        {
          action: 'set_velocity',
          session_id: session.id,
          seq: nextSeq(session),
          vx: pending.vx,
          vy: pending.vy,
          vz: pending.vz,
          yaw_rate: pending.yawRate,
          frame: pending.frame ?? 'map',
          ttl_ms: Math.min(pending.ttlMs ?? MANUAL_LIMITS.TTL_HOLD_MS, MANUAL_LIMITS.TTL_MAX_MS),
        },
        // Bounded at the cadence that replaces it, so a stalled link cannot
        // accumulate frames nobody is waiting on. A stop is deliberately NOT
        // bounded (`sendHoverNow`): nothing re-sends it, so abandoning one
        // would be abandoning the stop itself.
        { timeoutMs: MANUAL_SETPOINT_TIMEOUT_MS },
      ).catch((err) => handleFrameError(err, epoch));
      // A held vector has to be re-sent or the RC zeroes it at the ttl. This is
      // the renewal, and it is what makes "hold the pointer still" mean "keep
      // flying" rather than "fly for 400 ms".
      scheduleFlushRef.current();
    }, waitMs);
  }, [handleFrameError, nextSeq, post, postStopFrame]);
  scheduleFlushRef.current = scheduleFlush;

  const hover = useCallback(() => {
    if (!sessionRef.current) return;
    heldKeysRef.current.clear();
    if (mountedRef.current) {
      setKeyAxes(NO_AXES);
      // The keys are disarmed above. This is how the POINTER gets disarmed too:
      // the surface owns that state and watches this counter. Bumped before the
      // frame goes out, so the stop and the disarm are one commit.
      setStopSignal((n) => n + 1);
    }
    sendHoverNow();
  }, [sendHoverNow]);

  const send = useCallback(
    (frame: ManualFrame) => {
      const session = sessionRef.current;
      if (!session) return;

      const isZero =
        frame.vx === 0 && frame.vy === 0 && frame.vz === 0 && frame.yawRate === 0;
      if (isZero) {
        // An all-zero `set_velocity` and a `hover` are the same command; the
        // contract keeps `hover` as its own verb so a stop is legible in a log.
        // Sent only on the transition, so a resting surface is silent while the
        // heartbeat alone keeps the session alive.
        if (pendingRef.current === null) return;
        sendHoverNow();
        return;
      }

      pendingRef.current = frame;
      // The operator is flying again, so the stop no longer stands: the next
      // release of the control is a new stop and not a repeat, and a stop still
      // parked on the flush timer is superseded by this vector the same way one
      // pending vector supersedes another.
      stopStandsRef.current = false;
      pendingStopRef.current = false;
      if (mountedRef.current) setState((prev) => (prev === 'HOVER' ? 'LIVE' : prev));
      // `scheduleFlush` is a no-op while a flush is already queued, so a pointer
      // moving at display rate replaces the pending frame rather than queueing
      // one send per pixel — and a pointer held perfectly still keeps being
      // re-sent by that same timer, which is what stops the RC zeroing it at the
      // ttl. Both behaviours fall out of leaving the interval alone here.
      scheduleFlush();
    },
    [scheduleFlush, sendHoverNow],
  );

  // -------------------------------------------------------------------------
  // Session plane
  // -------------------------------------------------------------------------

  /**
   * Ensure the heartbeat is running. IDEMPOTENT, and that is the whole point.
   *
   * `onGranted` is called from every confirming `control_state` frame, not only
   * from the grant ack — and the RC publishes those at 1 Hz while it holds
   * authority. Restarting the interval here cleared the pending timer a beat
   * before it fired, so the heartbeat almost never landed: the authority lock
   * (renewed by the heartbeat and nothing else) lapsed at its 10 s TTL and the
   * RC's own dead-man handed the aircraft back mid-flight, which reached the
   * operator as `NO_SESSION_REFUSAL` on their next setpoint. Observed on dev as
   * one beat in 17.8 s against a nominal 1 Hz.
   *
   * Restarting also reset `heartbeatRefusalsRef` on every frame, so
   * MANUAL_HEARTBEAT_REFUSAL_LIMIT consecutive refusals could never accumulate
   * while the RC was still reporting — the console would never give up on a
   * lock it had stopped renewing. Both follow from the same line.
   *
   * A session that has genuinely ended always clears the interval first
   * (`endSessionLocally`, `release`, and the drone/unmount cleanup all call
   * `stopHeartbeat`), so "already running" only ever means "this same session is
   * already beating".
   */
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current !== null) return;
    heartbeatRefusalsRef.current = 0;
    heartbeatWarningRef.current = null;
    heartbeatRef.current = setInterval(() => {
      const session = sessionRef.current;
      if (!session) {
        stopHeartbeat();
        return;
      }
      const epoch = epochRef.current;
      void post(
        {
          action: 'heartbeat',
          session_id: session.id,
          seq: nextSeq(session),
        },
        { timeoutMs: MANUAL_HEARTBEAT_REQUEST_TIMEOUT_MS },
      )
        .then(() => noteHeartbeatDelivered(epoch))
        .catch((err) => handleHeartbeatError(err, epoch));
    }, MANUAL_LIMITS.HEARTBEAT_INTERVAL_MS);
  }, [handleHeartbeatError, noteHeartbeatDelivered, nextSeq, post, stopHeartbeat]);

  /**
   * Authority is ours. Called from whichever confirmation lands first: the RC's
   * `granted` ack on the command plane, or its first `control_state` frame. Both
   * are real confirmations and either may win the race, so this is idempotent.
   */
  const onGranted = useCallback(() => {
    clearGrantTimer();
    if (!sessionRef.current) return;
    if (!mountedRef.current) return;
    // The grant IS evidence the RC is on the other end, so it starts the COP's
    // own dead-man. Without a baseline here that timer would have nothing to
    // measure from until the first `control_state` frame — and an RC that grants
    // and then goes silent is precisely the case it exists for.
    rcSignalAtRef.current = Date.now();
    setState((prev) => (prev === 'REQUESTED' ? 'HOVER' : prev));
    setHeldSince((prev) => prev ?? Date.now());
    startHeartbeat();
  }, [clearGrantTimer, startHeartbeat]);

  /**
   * Hand the aircraft back, and RESOLVE WITH WHETHER THAT LANDED.
   *
   * The answer is not decoration. The RC refuses `takeoff`, `start_mission` and
   * `goto` while a manual session holds the aircraft, so the COP's `FLY TO HERE`
   * has to release first and may only dispatch on a release that was actually
   * delivered — a release that 500s or times out leaves the RC holding authority
   * until its own dead-man expires, and a `goto` sent into that window comes back
   * rejected. False is therefore "do not command this aircraft yet", not "warn
   * and carry on".
   */
  const release = useCallback(
    async (why: ManualReleaseReason = 'operator'): Promise<boolean> => {
      const session = sessionRef.current;
      const id = droneIdRef.current;
      if (!session || !id) {
        // Nothing held is not a failed release: the aircraft is already free of
        // this console, which is exactly what the caller is waiting to hear.
        endSessionLocally('IDLE', null);
        return true;
      }

      // The session is over from this console's point of view the moment the
      // operator asked, so the ref goes first: it is what every timer, the
      // pending flush and a second press all read, and leaving it set for the
      // length of the stop below would let an Escape held down dispatch two
      // hand-backs for one session.
      // The epoch this hand-back owns. Everything below resolves on the wire —
      // a stop, then a release — and the operator can have taken ANOTHER
      // aircraft by the time it does. Writing IDLE then collapses that live
      // session's UI while `sessionRef` still holds it and the heartbeat keeps
      // renewing its lock: the MANUAL key becomes a permanent no-op (`request`
      // refuses while a session exists) and the console reads IDLE over an
      // aircraft it is still flying. So every write past here is gated.
      const epoch = setSession(null);
      stopHeartbeat();
      clearGrantTimer();
      clearFlush();
      heldKeysRef.current.clear();
      heartbeatRefusalsRef.current = 0;
      heartbeatWarningRef.current = null;
      rcSignalAtRef.current = null;
      if (mountedRef.current) {
        setKeyAxes(NO_AXES);
        setState('RELEASING');
        setBusy(true);
      }

      // Stop, THEN hand back — in that order ON THE WIRE, not merely in that
      // order in the source. Fired together they raced, and a server that
      // answered the release first turned the stop into a 409 for a session
      // that no longer existed: `handleFrameError` then toasted "no manual
      // control session" and overwrote the reason of a hand-back that had
      // actually succeeded. Errors here are swallowed for the same reason —
      // the release below is the outcome the operator is waiting on, and a
      // courtesy stop that failed is not news on the way out. The bound is what
      // keeps a stalled stop from holding the hand-back for ever
      // (HOVER_BEFORE_RELEASE_MAX_MS).
      await settledWithin(
        post({ action: 'hover', session_id: session.id, seq: (session.seq += 1) }).catch(
          () => undefined,
        ),
        HOVER_BEFORE_RELEASE_MAX_MS,
      );

      let delivered = true;
      try {
        await api.post(
          `/api/drones/${id}/manual-control`,
          {
            action: 'release',
            session_id: session.id,
            reason: why,
            layout: layoutRef.current,
          },
          // Bounded, for the same reason the stop above it is: this promise is
          // what `dispatchManualFlyTo` awaits before it dispatches, and that in
          // turn is inside the try/finally that clears the FLY TO HERE strip. A
          // release that never settled left `flyToInFlightRef` and `flyToBusy`
          // true for the life of the page — CONFIRM disabled and every later
          // FLY TO HERE turned away at the guard, with no way back short of a
          // reload. Past the bound it is UNDELIVERED, which is what it is.
          { timeoutMs: MANUAL_RELEASE_TIMEOUT_MS },
        );
        if (mountedRef.current && epochRef.current === epoch) setReason(null);
      } catch (err) {
        // "Nobody holds this aircraft" is not a failed release — it is the
        // answer the caller is waiting for. The backend returns it (409) when
        // the lock has already lapsed or the RC ended the session first, and
        // both mean the aircraft is free of this console, which is the whole
        // question `delivered` asks. Reported as a failure it told the operator
        // the hand-back did not work for an aircraft nothing was holding, and
        // `dispatchManualFlyTo` then refused to send a GO-TO that would have
        // flown.
        const alreadyFree =
          err instanceof ApiRequestError &&
          err.status === 409 &&
          isNothingToReleaseRefusal(err.message);
        delivered = alreadyFree;
        // The lock is gone locally either way; say plainly that the RC keeps
        // authority until its own dead-man expires rather than implying the
        // aircraft is free.
        if (mountedRef.current && epochRef.current === epoch) {
          setReason(
            alreadyFree
              ? null
              // Only an answer the API actually gave contributes its own
              // sentence. "The operation was aborted due to timeout" and
              // "Failed to fetch" are the platform talking to a developer, and
              // what they both mean to an operator is already written down.
              : err instanceof ApiRequestError
                ? getApiErrorMessage(err, MANUAL_RELEASE_UNDELIVERED_REASON)
                : MANUAL_RELEASE_UNDELIVERED_REASON,
          );
        }
      } finally {
        if (mountedRef.current && epochRef.current === epoch) {
          setBusy(false);
          setSessionId(null);
          setHeldSince(null);
          setTtlRemainingMs(null);
          setLastStateAtMs(null);
          setClamped([]);
          setClampDetail(null);
          // IDLE even on a failed release, and deliberately: this console has
          // stopped renewing the lock, so rendering LIVE over it would be the
          // exact claim the whole file is arranged to never make.
          setState('IDLE');
        }
      }
      return delivered;
    },
    [clearFlush, clearGrantTimer, endSessionLocally, post, setSession, stopHeartbeat],
  );

  const releaseRef = useRef(release);
  releaseRef.current = release;

  /**
   * Measure the link before asking for the aircraft.
   *
   * `/ready` is the cheapest thing the API answers — a static 200 with no
   * database, no Redis and no rate limiter — so this times the path and not the
   * work. The figure is advisory by construction (a client that under-reports
   * only deceives its own operator), which is exactly why it is measured rather
   * than assumed: the alternative is no latency gate at all, because nothing on
   * the server can time the RC's radio link.
   *
   * A probe that fails returns null and the request goes without `rtt_ms`. The
   * backend treats it as absent rather than as zero, which is honest: we did not
   * measure it.
   */
  const probeRtt = useCallback(async (): Promise<number | null> => {
    const startedAt = Date.now();
    try {
      await api.get('/ready');
      return Date.now() - startedAt;
    } catch {
      return null;
    }
  }, []);

  const request = useCallback(() => {
    const id = droneIdRef.current;
    if (!id) return;
    // The gate the `available` prop has always claimed. Deliberately silent: the
    // caller is a control that should already be disabled, so there is nothing
    // for an operator to read here and no state to move them out of. Everything
    // they need is on the key itself, in `unavailableReason`.
    if (!availableRef.current) return;
    if (sessionRef.current || stateRef.current === 'REQUESTED') return;

    setBusy(true);
    setState('REQUESTED');
    setReason(null);
    setClamped([]);
    setClampDetail(null);

    void (async () => {
      // The epoch this request belongs to. If the operator selects a different
      // aircraft while it is in flight, the drone-change cleanup ends things
      // here — and this continuation must not then write REQUESTED-era state
      // (or, worse, ask for a session on whichever aircraft is selected now,
      // since `post` reads the CURRENT drone id).
      const epoch = epochRef.current;
      const measured = await probeRtt();
      if (!mountedRef.current || epochRef.current !== epoch) return;
      if (measured !== null) setRttMs(measured);

      // Refused in the browser as well as at the API, so the operator gets the
      // number back immediately instead of after a round trip that proves the
      // point. Same sentence either way — the string is the contract's.
      if (measured !== null && measured > MANUAL_LIMITS.RTT_REFUSE_MS) {
        setState('IDLE');
        setReason(refusalSlowLink(measured));
        setBusy(false);
        toast.error(refusalSlowLink(measured));
        return;
      }

      try {
        const data = await post<{
          session_id: string;
          command_id: string;
          envelope: ManualEnvelope;
          grant_timeout_ms?: number;
          warning?: string;
        }>({
          action: 'request',
          ...(measured !== null ? { rtt_ms: measured } : {}),
          yaw_rate_dps: yawRateDps,
          layout: layoutRef.current,
        });
        if (!data) {
          setState('IDLE');
          setBusy(false);
          return;
        }
        // The operator may have moved on while this was in flight. Releasing
        // straight away is the only honest outcome: a session exists on an
        // aircraft nobody is looking at.
        if (droneIdRef.current !== id || !mountedRef.current || epochRef.current !== epoch) {
          abandonSession(
            id,
            { id: data.session_id, commandId: data.command_id, seq: 0, adopted: false },
            'operator',
          );
          return;
        }

        setSession({ id: data.session_id, commandId: data.command_id, seq: 0, adopted: false });
        seqAheadRef.current = null;
        setSessionId(data.session_id);
        setEnvelope(data.envelope);
        setBusy(false);
        if (data.warning) setReason(data.warning);

        // The RC grants or denies; nothing here can. Silence is its own answer
        // after this window, and it frees the lock rather than leaving the
        // aircraft claimed by a session that never existed.
        clearGrantTimer();
        grantTimerRef.current = setTimeout(() => {
          grantTimerRef.current = null;
          const session = sessionRef.current;
          if (!session || !HOLDING.has(stateRef.current)) {
            // Locally first — same reason as `abortSession`: the hand-back
            // speaks only for the epoch of a console holding nothing.
            endSessionLocally('IDLE', MANUAL_NO_ANSWER_REASON);
            if (session) abandonSession(id, session, 'timeout');
            toast.error(MANUAL_NO_ANSWER_REASON);
          }
        }, data.grant_timeout_ms ?? MANUAL_LIMITS.GRANT_TIMEOUT_MS);
      } catch (err) {
        // A refusal for a request the operator has already moved on from must
        // not clear `sessionRef` — by now it may hold the session they are
        // actually flying.
        if (!mountedRef.current || epochRef.current !== epoch) return;
        const message = getApiErrorMessage(err, 'Manual control was refused.');
        setSession(null);
        setState('IDLE');
        setReason(message);
        setBusy(false);
        toast.error(message);
      }
    })();
  }, [abandonSession, clearGrantTimer, endSessionLocally, post, probeRtt, setSession, yawRateDps]);

  // -------------------------------------------------------------------------
  // RC → COP
  // -------------------------------------------------------------------------

  useEffect(() => {
    return subscribeDroneAck((ack) => {
      const session = sessionRef.current;
      if (!session || ack.drone_id !== droneIdRef.current) return;
      if (ack.command_id !== session.commandId) return;

      if (ack.status === 'granted') {
        onGranted();
        return;
      }
      // `denied` carries a reason the operator can act on; `timed_out` is the
      // app admitting it never reached the aircraft. Neither leaves a session.
      if (ack.status === 'denied' || ack.status === 'rejected' || ack.status === 'timed_out') {
        const message = ack.detail?.trim() || MANUAL_NO_ANSWER_REASON;
        endSessionLocally('IDLE', message);
        toast.error(message);
      }
    });
  }, [endSessionLocally, onGranted, subscribeDroneAck]);

  useEffect(() => {
    return subscribeManualControlState((frame) => {
      if (frame.drone_id !== droneIdRef.current) return;
      const session = sessionRef.current;
      if (!session || frame.session_id !== session.id) return;

      // Is somebody ELSE flying this session? Asked before anything in the frame
      // is believed, because every field below describes an aircraft that would
      // then be answering to another screen.
      //
      // `session.seq` is the highest seq this console has put on the wire, and
      // the RC only ever advances `seq_ack` to a seq it ACCEPTED — so a
      // `seq_ack` beyond ours came from frames we did not send. That is the
      // rehydration hole: the three adoption conditions are all true of a
      // session another tab on the same account is actively driving, and the
      // adopting console then renders HOVER, renders LIVE on a drag, renews the
      // lock with its heartbeats, and moves nothing at all, because the RC drops
      // every one of its frames as stale.
      //
      // ADOPTED sessions only. A granted session id is minted per request and is
      // nobody else's to drive, so the same evidence there could only come from
      // an app reporting a counter this COP has misread — and ending a session
      // the operator is genuinely flying is a far worse error than the one this
      // check exists to prevent, which costs a re-press.
      //
      // TWO frames, each further ahead than the last, rather than one. A single
      // frame ahead is also what this console's OWN previous page load looks
      // like: its `beforeunload` stop can land after the snapshot was read, so a
      // resumed session legitimately starts one seq behind reality and catches
      // up on its next heartbeat. A second sender keeps going; a residue does
      // not, which is exactly the difference these two frames measure.
      //
      // WHAT THIS DOES NOT SEE: a second console that is merely HOVERING.
      // Both consoles derive `seq` from the same rehydrated base and both
      // heartbeat at 1 Hz, so a console that is holding station never advances
      // `seq_ack` past this one — only one actively DRAGGING pulls ahead. Read
      // this check as "somebody else is FLYING it right now", never as "somebody
      // else has it". The gap is coverage, not correctness: a hovering second
      // console is found the moment it moves the aircraft, and until then both
      // screens agree with what the aircraft is doing, which is nothing.
      //
      // The cheap real fix when it becomes worth doing: have the RC report the
      // SENDER of the last accepted frame on `control_state` — or round-trip a
      // per-console nonce sent with each setpoint — so ownership is read
      // directly instead of inferred from a counter both consoles share.
      if (session.adopted && typeof frame.seq_ack === 'number' && frame.seq_ack > session.seq) {
        const previous = seqAheadRef.current;
        seqAheadRef.current = frame.seq_ack;
        if (previous !== null && frame.seq_ack > previous) {
          // Local only. A `release` names the session and the backend honours it
          // from the account holding it, so handing back here would end the
          // flight of whoever is actually flying it.
          endSessionLocally('IDLE', MANUAL_FOREIGN_SESSION_REASON);
          toast.error(MANUAL_FOREIGN_SESSION_REASON);
          return;
        }
      } else {
        seqAheadRef.current = null;
      }

      // Stamped on arrival, before anything else this frame says is believed.
      // The RC publishes at 1 Hz while it holds authority, so the ABSENCE of
      // this stamp moving is the only evidence the COP has that an RC has
      // stopped reporting — the last frame's `authority: "live"` stays true
      // looking forever, and a banner reading LIVE beside a drained dead-man is
      // exactly the ambiguity the banner exists to remove.
      rcSignalAtRef.current = Date.now();
      setLastStateAtMs(Date.now());
      setTtlRemainingMs(frame.ttl_remaining_ms);
      setClamped(frame.clamped as ManualClampReason[]);
      setClampDetail(frame.detail);
      setPilotOverride(frame.pilot_override);

      switch (frame.authority) {
        case 'live':
        case 'hover':
          // The first state frame is as good a grant confirmation as the ack —
          // and on a link that dropped the ack it is the only one.
          onGranted();
          break;
        case 'override':
          endSessionLocally('OVERRIDE', frame.detail ?? 'Pilot took the sticks — authority revoked at the RC.');
          setPilotOverride(true);
          toast.warning('Pilot override — manual authority revoked at the RC.');
          break;
        case 'fault':
          endSessionLocally('FAULT', frame.detail ?? 'The RC reported a fault and gave the aircraft back.');
          toast.error('Manual control fault — authority returned to the RC.');
          break;
        case 'released':
          endSessionLocally(
            'IDLE',
            frame.detail ?? 'The RC ended the manual session — heartbeat dead-man expired.',
          );
          break;
        default:
          // An app build with a word this COP has never heard of. Render it
          // rather than dropping the frame: the backend deliberately keeps the
          // vocabulary open, and a session shown as nothing is the worse failure.
          setReason(frame.detail ?? `RC reports authority "${frame.authority}".`);
      }
    });
  }, [endSessionLocally, onGranted, subscribeManualControlState]);

  /**
   * The COP's own dead-man.
   *
   * Everything else in this file trusts the RC to end a session it can no longer
   * serve — its heartbeat timeout zeroes the aircraft, and it publishes a
   * terminal `control_state`. But that frame is published over the same broker
   * link that just failed: the RC discards its terminal frame when its link is
   * down and nothing re-publishes it on reconnect. The COP would then heartbeat
   * a backend lock for ever, over a green banner, while every drag is a no-op.
   *
   * So silence is measured here and acted on here. The banner already SHOWS it
   * at `MANUAL_RC_SILENT_MS`; this is the point at which showing it is no longer
   * enough, and the threshold is the RC's own dead-man because past it the
   * aircraft has certainly been handed back at the RC.
   *
   * The baseline is `rcSignalAtRef`, not `lastStateAtMs`: a grant with no frame
   * behind it yet is still evidence, and starting the clock at the grant is what
   * catches an RC that answers once and then goes quiet.
   */
  useEffect(() => {
    if (!holdsAuthority) return;
    const timer = setInterval(() => {
      const since = rcSignalAtRef.current;
      if (since === null) return;
      if (Date.now() - since <= MANUAL_LIMITS.HEARTBEAT_TIMEOUT_MS) return;
      abortSession(MANUAL_RC_LOST_REASON, 'timeout');
    }, DEADMAN_TICK_MS);
    return () => clearInterval(timer);
  }, [abortSession, holdsAuthority]);

  // -------------------------------------------------------------------------
  // Rehydration — a COP that reloaded mid-session
  // -------------------------------------------------------------------------

  /**
   * Pick a session back up after a page reload.
   *
   * `GET /api/drones/:id` carries `manual_control` for exactly this: without it
   * a reload leaves the operator reading their OWN broadcast session back as a
   * foreign hold, with the MANUAL key refused in their own name until the lock's
   * 10 s TTL expires. The endpoint existed and had no consumer.
   *
   * Three conditions, and each one is a safety property rather than a nicety:
   *
   *  1. **The holder must be this account.** Authority is never inherited from a
   *     page load; somebody else's session stays somebody else's.
   *  2. **The RC must still be in it** — `live` or `hover`. A `released`,
   *     `override` or `fault` frame is a session that has already ended.
   *  3. **`seq_ack` must be known.** The RC drops any frame whose `seq` is not
   *     greater than the last it accepted, so a session resumed at seq 0 would
   *     render LIVE and silently move nothing for hundreds of frames. Without
   *     that number there is nothing safe to resume, so the session is left
   *     alone — the operator is still not locked out (the COP knows the hold is
   *     theirs) and pressing MANUAL re-requests, which the backend grants by
   *     dropping their own lock and taking a clean one.
   *
   * All three are ALSO true of a session another tab on this account is flying
   * right now, and no snapshot can tell the two apart: a live session and a
   * left-behind one look identical from here. So adoption is provisional, and
   * the `seq_ack` check in the `control_state` subscriber above is what settles
   * it — a second sender keeps pushing `seq_ack` past everything this console
   * has sent, and that ends the session here rather than pretending to fly.
   *
   * The envelope is deliberately NOT invented here. The control surface falls
   * back to the org's own limits when it is null, and the hard envelope is the
   * RC's anyway — the browser's copy is a hint.
   */
  useEffect(() => {
    const holder = rehydrate?.holder ?? null;
    const rcState = rehydrate?.state ?? null;
    if (!holder || !droneId) return;
    if (!selfUserId || holder.user_id !== selfUserId) return;
    if (adoptedRef.current.has(holder.session_id)) return;
    // Something is already going on locally — a live session, or a request in
    // flight. A snapshot is only ever the answer to "what did we come back to".
    if (sessionRef.current || stateRef.current === 'REQUESTED' || stateRef.current === 'RELEASING') {
      return;
    }
    if (!rcState || rcState.session_id !== holder.session_id) return;
    if (!RESUMABLE_AUTHORITY.has(rcState.authority)) return;
    if (typeof rcState.seq_ack !== 'number') return;
    // Read out here, where the guard above has narrowed it: the adoption runs
    // in a closure a tick later, and TypeScript widens a property back to its
    // declared type across that boundary.
    const seqAck = rcState.seq_ack;

    // SCHEDULED, not done here, and the cleanup below cancels it. That pair is
    // what makes this effect symmetric, and it has to be: React StrictMode
    // double-invokes every effect in development, so an adoption performed in
    // the body is immediately followed by the DRONE-CHANGE cleanup further down
    // — which sees a session, ends it locally and hands the aircraft back on the
    // wire for real (a hover and a release, to the RC, over a session the
    // operator never left). The second pass then found `adoptedRef` already
    // holding the id and never re-adopted, so rehydration could not be tested
    // anywhere StrictMode is on: dev.shovron.com and every worktree preview,
    // which is exactly where it is exercised against the companion sim.
    //
    // Deferring by a microtask costs one tick and makes the double-invoke a true
    // no-op: the first pass's adoption is cancelled before it happens, so the
    // cleanup pass finds no session to hand back, and the second pass adopts
    // once. A GENUINE teardown — a drone change, an unmount — cancels an
    // adoption that has not run yet and leaves an adoption that has, so the
    // drone-change cleanup still hands back on the wire exactly as before.
    let cancelled = false;
    queueMicrotask(() => {
      // The tick this waited may have been the tick something else took the
      // aircraft. Cheap, and the alternative is adopting over a live session.
      if (cancelled || !mountedRef.current) return;
      if (sessionRef.current || adoptedRef.current.has(holder.session_id)) return;
      adoptedRef.current.add(holder.session_id);
      setSession({
        id: holder.session_id,
        // No grant is pending, so nothing can be matched against this: the ack
        // subscriber compares command ids and an empty one matches nothing, which
        // is the correct behaviour for a session that was granted last page load.
        commandId: '',
        seq: seqAck,
        adopted: true,
      });
      seqAheadRef.current = null;
      const acquiredAtMs = Date.parse(holder.acquired_at);
      // Stamped NOW rather than from the snapshot: this is the first moment this
      // browser has heard anything, and the dead-man measures OUR silence.
      rcSignalAtRef.current = Date.now();
      setSessionId(holder.session_id);
      setHeldSince(Number.isNaN(acquiredAtMs) ? Date.now() : acquiredAtMs);
      setTtlRemainingMs(rcState.ttl_remaining_ms);
      setClamped(rcState.clamped as ManualClampReason[]);
      setClampDetail(rcState.detail);
      setPilotOverride(rcState.pilot_override);
      setReason(MANUAL_REHYDRATED_REASON);
      // HOVER even when the RC last reported `live`. LIVE means a vector is being
      // streamed, and this console is streaming nothing: the previous page load's
      // last setpoint expires at its 400 ms ttl and the RC zeroes it. The aircraft
      // is station-keeping under our authority, which is exactly what HOVER means,
      // and the RC's next frame corrects it in a second either way.
      setState('HOVER');
      startHeartbeat();
    });
    return () => {
      cancelled = true;
    };
  }, [droneId, rehydrate, selfUserId, setSession, startHeartbeat]);

  // -------------------------------------------------------------------------
  // The ways an operator stops paying attention
  // -------------------------------------------------------------------------

  /**
   * Alt-tab, a click into another window, the screen locking, a phone call.
   *
   * None of them are the operator saying "keep flying". The aircraft goes to a
   * hover and the session stays alive, so coming back is one gesture rather than
   * a re-request — but nothing keeps moving while nobody is watching it.
   */
  useEffect(() => {
    if (!holdsAuthority) return;
    const toHover = () => hover();
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') hover();
    };
    window.addEventListener('blur', toHover);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', toHover);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [holdsAuthority, hover]);

  /**
   * The tab is closing, or being reloaded, with the aircraft in hand.
   *
   * Neither runs a React unmount, so the drone-change cleanup below never fires
   * and the authority lock would sit there until the RC's 10 s heartbeat dead-man
   * expired. The operator who reloads and immediately retries is then refused by
   * THEIR OWN previous session — which reads as somebody else having taken their
   * aircraft. Both frames go out with `keepalive` so the browser is allowed to
   * finish them after the document is gone; it is still best-effort, which is
   * why the RC's dead-man remains the real backstop.
   *
   * `pagehide` as well as `beforeunload` because mobile Safari fires only the
   * former, and a tablet is the target device. Firing both is safe: the second
   * finds no session and does nothing.
   *
   * Armed from REQUESTED as well as from the two holding states, and not only
   * from `holdsAuthority`: a session EXISTS from the moment `request`'s POST
   * returns, and the grant it is waiting for may be up to `grant_timeout_ms`
   * (3 s) behind that. A tab closed inside that window sent no release at all
   * and left the lock held for its full TTL — the operator who reloads is then
   * refused by their own previous session. Same condition the drone-change
   * cleanup already uses (`wasEngaged`).
   */
  useEffect(() => {
    if (!engaged) return;
    const onLeaving = () => {
      const session = sessionRef.current;
      const id = droneIdRef.current;
      if (!session || !id) return;
      setSession(null);
      abandonSession(id, session, 'operator', { unload: true });
      // Said as well as sent: a page restored from the back-forward cache comes
      // back with no session, and must not come back rendering LIVE.
      endSessionLocally('IDLE', MANUAL_PAGE_UNLOAD_REASON);
    };
    window.addEventListener('beforeunload', onLeaving);
    window.addEventListener('pagehide', onLeaving);
    return () => {
      window.removeEventListener('beforeunload', onLeaving);
      window.removeEventListener('pagehide', onLeaving);
    };
  }, [abandonSession, endSessionLocally, engaged, setSession]);

  /** Flight keys are momentary: keyup ends the axis it started. */
  useEffect(() => {
    if (!holdsAuthority) return;
    const onKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (!heldKeysRef.current.has(key)) return;
      heldKeysRef.current.delete(key);
      setKeyAxes(axesFromHeldKeys(heldKeysRef.current));
    };
    // A key held while focus leaves never delivers its keyup, so the axis would
    // stay latched and the aircraft would keep flying on a key nobody is
    // pressing. The blur handler above already hovers; this clears the latch so
    // returning to the window does not resume the old vector.
    const onBlur = () => {
      if (heldKeysRef.current.size === 0) return;
      heldKeysRef.current.clear();
      setKeyAxes(NO_AXES);
    };
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [holdsAuthority]);

  /**
   * The aircraft changed, or the COP unmounted.
   *
   * Both leave a live lease on an aircraft nobody is flying. The cleanup runs
   * with the OLD drone id captured in the closure — `droneIdRef` already points
   * at the new selection by then — and cannot await, so it fires both frames and
   * lets the browser finish them.
   *
   * It also ENDS THE SESSION LOCALLY, which is the whole point and used to be
   * missing. Releasing on the wire while leaving `state` alone left the COP
   * rendering LIVE, the full control ring, "RC locked out" and a green banner
   * over the newly selected aircraft — with every drag, every flight key and
   * STOP itself a silent no-op, because `sessionRef` was already null. The RC's
   * own `released` frame could not correct it either, for the same reason. The
   * only cue was the dead-man draining to 0.0s, which reads as a link problem.
   *
   * `endSessionLocally` is a no-op past `mountedRef`, so the unmount path is
   * unchanged: it clears the timers and the refs and touches no state.
   */
  useEffect(() => {
    const id = droneId;
    return () => {
      const session = sessionRef.current;
      // REQUESTED with no session yet is a `request` still in flight. Its own
      // continuation abandons the session it is about to be handed (it compares
      // `droneIdRef` against the id it asked for), but nothing there resets the
      // state — so without this the bar sits on REQUESTING for ever.
      const wasEngaged = session !== null || stateRef.current === 'REQUESTED';
      stopHeartbeat();
      clearGrantTimer();
      clearFlush();
      if (wasEngaged) {
        // Locally first, then on the wire: `endSessionLocally` is what nulls
        // `sessionRef` and moves the epoch, and the hand-back's own failure is
        // only allowed to speak for a console that holds nothing.
        endSessionLocally('IDLE', MANUAL_DRONE_CHANGED_REASON);
        if (session && id) abandonSession(id, session, 'operator');
      }
      // Nothing was engaged, so there is nothing to end — but a refusal left
      // over from the PREVIOUS aircraft is a sentence about an aircraft the
      // operator is no longer looking at, and the surfaces that render it name
      // whichever one is selected now. It belongs to the selection that earned
      // it. (No-op past unmount, like everything else on this path.)
      else if (mountedRef.current) setReason(null);
    };
  }, [
    droneId,
    abandonSession,
    clearFlush,
    clearGrantTimer,
    endSessionLocally,
    setSession,
    stopHeartbeat,
  ]);

  // Nothing selected, or the aircraft cannot be flown by hand: say which, and
  // only while no session is held. An aircraft that goes stale mid-session is
  // still under our authority and must not be renamed UNAVAILABLE on screen —
  // the RC's dead-man is what ends that session, not a client-side label.
  //
  // Gated on the rendered values rather than on refs, and they are in the
  // dependency list. Refs are written during render, so this effect used to be
  // able to run in a commit where `holdsAuthorityRef` still said true for a
  // session that had just ended, return early, and never be re-run — which is
  // one half of how a drone switch left the COP claiming authority it did not
  // hold. Reading state means the effect runs again when authority actually ends.
  useEffect(() => {
    if (holdsAuthority || sessionId !== null || sessionRef.current) return;
    setState((prev) => {
      if (prev === 'REQUESTED' || prev === 'RELEASING') return prev;
      if (!available) return 'UNAVAILABLE';
      return prev === 'UNAVAILABLE' ? 'IDLE' : prev;
    });
  }, [available, droneId, holdsAuthority, sessionId]);

  // -------------------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!holdsAuthorityRef.current) return false;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
      // The OS repeats a held key at its own rate. Every handler below is a
      // state change, not a command, so a repeat would be a no-op — except that
      // it would also claim the event from the command bar thirty times a
      // second. Let it through untouched.
      if (event.repeat) return false;

      const key = event.key.toLowerCase();

      // STOP. Not a release: the operator wants the aircraft still, and still
      // theirs.
      if (key === ' ' || key === 'spacebar') {
        hover();
        return true;
      }
      // Stop AND hand back — the two-part panic key, in that order.
      if (key === 'escape') {
        // Nothing here waits on the outcome — the panic key is not a place to
        // block. A release that fails still says so, in `reason`.
        void releaseRef.current('operator');
        return true;
      }

      const preset = MANUAL_LIMITS.SPEED_PRESETS_MS[Number(key) - 1];
      if (key >= '1' && key <= '3' && preset !== undefined) {
        setSpeedCapMs(preset);
        return true;
      }

      const mapped = AXIS_KEYS[key];
      if (!mapped) return false;
      heldKeysRef.current.add(key);
      setKeyAxes(axesFromHeldKeys(heldKeysRef.current));
      return true;
    },
    [hover],
  );

  return {
    state,
    label: MANUAL_STATE_LABEL[state],
    reason,
    unavailableReason: state === 'UNAVAILABLE' ? unavailableReason : null,
    sessionId,
    envelope,
    rttMs,
    ttlRemainingMs,
    lastStateAtMs,
    clamped,
    clampDetail,
    pilotOverride,
    heldSince,
    holdsAuthority,
    busy,
    keyAxes,
    speedCapMs,
    setSpeedCapMs,
    yawRateDps,
    request,
    release,
    send,
    hover,
    stopSignal,
    handleKeyDown,
  };
}

/**
 * Held keys → axes. Opposite keys cancel rather than the last one winning, which
 * is what a stick does when you push it both ways.
 */
function axesFromHeldKeys(held: ReadonlySet<string>): ManualKeyAxes {
  const axes: ManualKeyAxes = { ...NO_AXES };
  for (const key of held) {
    const mapped = AXIS_KEYS[key];
    if (mapped) axes[mapped.axis] += mapped.value;
  }
  return {
    forward: Math.max(-1, Math.min(1, axes.forward)),
    right: Math.max(-1, Math.min(1, axes.right)),
    up: Math.max(-1, Math.min(1, axes.up)),
    yaw: Math.max(-1, Math.min(1, axes.yaw)),
  };
}
